// src/roles/store/MaterialSummary.js

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { buildMaterialSummary } from "../../utils/materialSummary";

export default function MaterialSummary() {
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";

  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [filter,  setFilter]  = useState("all"); // all | low | zero
  const [attention, setAttention] = useState([]);
  const [tools,     setTools]     = useState([]);
  const [ignoredOldDocs, setIgnoredOldDocs] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { fetchSummary(); }, []);
  const fetchSummary = async () => {
    try {
      setLoading(true);

      // P4-3: all the matching lives in buildMaterialSummary() (matchItem, qty
      // only, Needs attention, Tools). Own + shared "" items, inactive ones too:
      // matchItem() needs them to keep a disabled item's lines hidden.
      const [itemsSnap, invSnap, mrfSnap] = await Promise.all([
        getDocs(query(collection(db, "items"), where("company_id", "in", [company_id, ""]))),
        getDocs(query(collection(db, "inventory_summary"), where("company_id", "==", company_id))),
        getDocs(query(collection(db, "material_requests"), where("company_id", "==", company_id))),
      ]);

      const summary = buildMaterialSummary({
        items:     itemsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        stockDocs: invSnap.docs.map(d => ({ id: d.id, data: d.data() })),
        mrfs:      mrfSnap.docs.map(d => d.data()),
        companyId: company_id,
      });
      setData(summary.rows);
      setAttention(summary.attention);
      setTools(summary.tools);
      setIgnoredOldDocs(summary.ignoredOldDocs);
    } catch (err) {
      console.error("MaterialSummary fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Derived: stock status ──────────────────────────────
  const getStatus = (item) => {
    if (item.in_stock === 0)                                      return "zero";
    if (item.minimum_stock > 0 && item.in_stock <= item.minimum_stock) return "low";
    return "ok";
  };

  const STATUS_UI = {
    zero: { label: "Out of Stock", bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
    low:  { label: "Low Stock",    bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },
    ok:   { label: "In Stock",     bg: "#dcfce7", color: "#166534", dot: "#22c55e" },
  };

  // ── Filter + Search ────────────────────────────────────
  const filtered = data.filter(item => {
    const matchSearch = item.item_name.toLowerCase().includes(search.toLowerCase()) ||
                        item.item_code.toLowerCase().includes(search.toLowerCase());
    const status = getStatus(item);
    const matchFilter = filter === "all" || filter === status;
    return matchSearch && matchFilter;
  });

  // ── Counts for filter tabs ─────────────────────────────
  const counts = {
    all:  data.length,
    low:  data.filter(i => getStatus(i) === "low").length,
    zero: data.filter(i => getStatus(i) === "zero").length,
  };

  if (loading) return <div className="p-6 text-slate-400">Loading inventory...</div>;

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <h1 className="text-2xl font-semibold">Material Summary</h1>
        <button
          onClick={fetchSummary}
          className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
          ↻ Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total Items"   value={data.length}                       color="#6366f1" />
        <StatCard label="In Stock"      value={data.filter(i => getStatus(i) === "ok").length}   color="#22c55e" />
        <StatCard label="Low Stock"     value={counts.low}                         color="#eab308" />
        <StatCard label="Out of Stock"  value={counts.zero}                        color="#ef4444" />
      </div>

      {/* Search + Filter */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search item name or code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-200 rounded-xl px-4 py-2 text-sm w-full md:w-72 outline-none focus:ring-2 focus:ring-indigo-200"
        />
        <div className="flex gap-2">
          {[
            { key: "all",  label: `All (${counts.all})` },
            { key: "low",  label: `⚠ Low (${counts.low})` },
            { key: "zero", label: `🔴 Zero (${counts.zero})` },
          ].map(f => (
            <button key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-xl text-sm font-semibold border transition ${
                filter === f.key
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {/* Table Header */}
        <div className="hidden md:grid md:grid-cols-8 bg-gray-900 text-white px-4 py-3 text-xs font-semibold uppercase tracking-wide">
          <div className="col-span-2">Item</div>
          <div className="text-center">Category</div>
          <div className="text-center">In Stock</div>
          <div className="text-center">Defective</div>
          <div className="text-center">Pending MRFs</div>
          <div className="text-center">Issued (All)</div>
          <div className="text-center">Status</div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-4xl mb-3">📦</div>
            No items found
          </div>
        ) : (
          filtered.map((item, i) => {
            const status = getStatus(item);
            const ui     = STATUS_UI[status];
            return (
              <div
                key={item.item_id}
                className={`border-b border-gray-50 px-4 py-3 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                style={{ borderLeft: `3px solid ${ui.dot}` }}
              >
                {/* Mobile layout */}
                <div className="md:hidden space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-sm text-gray-800">{item.item_name}</p>
                      <p className="text-xs text-gray-400">{item.item_code} {item.category && `• ${item.category}`}</p>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: ui.bg, color: ui.color }}>
                      {ui.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <MobileCell label="In Stock"    value={`${item.in_stock} ${item.unit}`}    bold />
                    <MobileCell label="Defective"   value={`${item.defective} ${item.unit}`} />
                    <MobileCell label="Pending MRF" value={`${item.pending_mrfs} ${item.unit}`}
                      highlight={item.pending_mrfs > 0} />
                  </div>
                </div>

                {/* Desktop layout */}
                <div className="hidden md:grid md:grid-cols-8 items-center text-sm">
                  <div className="col-span-2">
                    <p className="font-semibold text-gray-800">{item.item_name}</p>
                    <p className="text-xs text-gray-400">{item.item_code}</p>
                  </div>
                  <div className="text-center text-xs text-gray-500">{item.category || "—"}</div>
                  <div className="text-center font-semibold">{item.in_stock} <span className="text-xs text-gray-400">{item.unit}</span></div>
                  <div className="text-center text-red-500">{item.defective > 0 ? item.defective : "—"}</div>
                  <div className={`text-center font-semibold ${item.pending_mrfs > 0 ? "text-amber-600" : "text-gray-400"}`}>
                    {item.pending_mrfs > 0 ? item.pending_mrfs : "—"}
                  </div>
                  <div className="text-center text-gray-500">{item.approved_total || "—"}</div>
                  <div className="text-center">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: ui.bg, color: ui.color }}>
                      {ui.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <p className="text-xs text-gray-400 mt-3 text-right">
        {filtered.length} of {data.length} items • Last updated from live Firestore
        {ignoredOldDocs > 0 && ` • ${ignoredOldDocs} old stock records with no quantity ignored`}
      </p>

      {/* P4-3: what could not be placed on one item - never merged, never dropped */}
      {attention.length > 0 && (
        <div className="mt-5 bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <h2 className="text-sm font-semibold text-amber-800">Needs attention ({attention.length})</h2>
          <p className="text-xs text-amber-700 mb-3">These are not counted in the table above.</p>
          <div className="space-y-2">
            {attention.map(a => (
              <div key={`${a.reason}|${a.item_id}|${a.item_name}`} className="bg-white border border-amber-100 rounded-xl px-3 py-2 text-sm">
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-semibold text-gray-800">{a.item_name || a.item_id || "—"}</span>
                  <span className="text-xs text-gray-500">{a.source} • {a.qty} {a.unit}{a.lines > 1 ? ` • ${a.lines} lines` : ""}</span>
                </div>
                <p className="text-xs text-gray-400">{[a.item_id, a.item_code].filter(Boolean).join(" • ")}</p>
                <p className="text-xs text-amber-700">{a.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* P4-3: tool lines carry an asset id, so they are listed apart from Item Master */}
      {tools.length > 0 && (
        <div className="mt-5 bg-white border border-gray-100 rounded-2xl p-4">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Tools on MRFs ({tools.length})</h2>
          <div className="space-y-2">
            {tools.map(t => (
              <div key={`${t.item_code}|${t.item_name}`} className="flex flex-wrap justify-between gap-2 text-sm border-b border-gray-50 pb-2 last:border-0">
                <span>
                  <span className="font-medium text-gray-800">{t.item_name || "—"}</span>
                  {t.item_code && <span className="ml-2 text-xs text-gray-400">{t.item_code}</span>}
                </span>
                <span className="text-xs text-gray-500">{t.qty} {t.unit}{t.lines > 1 ? ` • ${t.lines} lines` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helper components ──────────────────────────────────────

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function MobileCell({ label, value, bold, highlight }) {
  return (
    <div className="bg-gray-50 rounded-lg p-2 text-center">
      <div className="text-gray-400 text-[10px] mb-0.5">{label}</div>
      <div className={`text-xs ${bold ? "font-bold text-gray-800" : ""} ${highlight ? "text-amber-600 font-semibold" : "text-gray-600"}`}>
        {value}
      </div>
    </div>
  );
}